import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  FaCheckCircle,
  FaCar,
  FaCalendarAlt,
  FaTachometerAlt,
  FaTag,
  FaClock,
  FaBarcode,
  FaCarAlt,
  FaPalette,
  FaDoorClosed,
  FaGasPump,
  FaCarSide,
  FaUsers,
  FaExchangeAlt,
  FaCogs,
  FaPhone,
  FaWhatsapp,
  FaFacebookF,
  FaLinkedin,
  FaEnvelope,
  FaShareAlt,
} from 'react-icons/fa';
import { FaGears } from 'react-icons/fa6';
import Skeleton from '../components/ui/Skeleton';
import RelatedCars from '../components/block/relatedCars';

export default function CarDetail() {
  const { slug } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const fetchListing = async () => {
      try {
        const response = await fetch('/api/listing');
        let data = await response.json();
        data = data.filter((item) => item._id === slug);
        if (data.length > 0) {
          setListing(data[0]);
          if (data[0].image) {
            setSelectedImage(0);
          }
        } else {
          setError("Car not found");
        }
      } catch (error) {
        setError("Failed to load the listing. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchListing();
  }, [slug]);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = listing ? `Check out this ${listing.title} on GIGA Mobility` : '';

  const handleShare = (platform) => {
    const url = encodeURIComponent(shareUrl);
    const text = encodeURIComponent(shareText);
    
    let shareLink = '';
    switch(platform) {
      case 'whatsapp':
        shareLink = `https://wa.me/?text=${text}%20${url}`;
        break;
      case 'facebook':
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'linkedin':
        shareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        break;
      case 'email':
        shareLink = `mailto:?subject=${text}&body=${text}%20${url}`;
        break;
    }
    if (shareLink) {
      window.open(shareLink, '_blank', 'width=600,height=400');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Skeleton className="w-16 h-16 rounded-full" />
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <p className="text-red-500 text-xl mb-4">{error || "Car not found"}</p>
          <a href="/cars" className="text-[#DC2626] hover:underline">
            Back to Cars
          </a>
        </div>
      </div>
    );
  }

  // Create image array (main image + gallery images if available)
  const galleryImages = listing.galleryImages || [];
  const images = listing.image ? [listing.image, ...galleryImages] : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-gray-50 py-8 md:py-12 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-red-200/10 rounded-full blur-3xl animate-pulse"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        {/* Title and Price Header */}
        <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8 lg:p-10 border border-gray-100 relative overflow-hidden mb-8">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-red-100/30 to-transparent rounded-bl-full"></div>
          
          <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-gradient-to-r from-red-50 to-orange-50 rounded-full border border-red-100">
                <span className="w-2 h-2 bg-[#DC2626] rounded-full animate-pulse"></span>
                <span className="text-sm font-bold text-[#DC2626] uppercase tracking-wider">
                  {listing.itemCondition || 'Available'}
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-4 leading-tight">
                {listing.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-lg md:text-xl text-gray-700 font-semibold">
                <span className="flex items-center gap-2">
                  <FaCalendarAlt className="text-[#DC2626]" />
                  {listing.year}
                </span>
                <span className="text-gray-400">•</span>
                <span className="flex items-center gap-2">
                  <FaPalette className="text-[#DC2626]" />
                  {listing.color}
                </span>
                <span className="text-gray-400">•</span>
                <span className="flex items-center gap-2">
                  <FaGasPump className="text-[#DC2626]" />
                  {listing.fuelType}
                </span>
              </div>
            </div>
            <div className="text-center md:text-right">
              <p className="text-4xl md:text-5xl lg:text-6xl font-extrabold bg-gradient-to-r from-[#DC2626] via-[#B91C1C] to-[#991B1B] bg-clip-text text-transparent mb-3">
                ${listing.price}
              </p>
              {listing.itemCondition && (
                <span className={`inline-block px-5 py-2 rounded-full text-base font-bold tracking-wide shadow-md ${
                  listing.itemCondition === 'Used' 
                    ? 'bg-orange-100 text-orange-800 border-2 border-orange-200' 
                    : 'bg-green-100 text-green-800 border-2 border-green-200'
                }`}>
                  {listing.itemCondition}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images and Overview */}
          <div className="lg:col-span-2 space-y-6">
            {/* Main Image */}
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100 relative group">
              <div className="relative aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-200">
                <img
                  src={images[selectedImage] || listing.image}
                  alt={listing.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>

              {/* Image Gallery Thumbnails */}
              {images.length > 1 && (
                <div className="p-4 md:p-6 bg-gradient-to-b from-gray-50 to-white border-t border-gray-200">
                  <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
                    {images.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImage(index)}
                        className={`flex-shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden border-2 transition-all duration-300 ${
                          selectedImage === index
                            ? 'border-[#DC2626] shadow-lg scale-105 ring-2 ring-red-200'
                            : 'border-gray-200 hover:border-red-300 hover:scale-105'
                        }`}
                      >
                        <img
                          src={img}
                          alt={`${listing.title} view ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Car Overview */}
            <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8 lg:p-10 border border-gray-100">
              <div className="flex items-center gap-4 mb-8">
                <div className="h-1.5 w-20 bg-gradient-to-r from-[#DC2626] to-[#B91C1C] rounded-full"></div>
                <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">Car Overview</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { icon: FaCar, label: "Make", value: listing.make },
                  { icon: FaCarAlt, label: "Model", value: listing.model },
                  { icon: FaCalendarAlt, label: "Year", value: listing.year },
                  { icon: FaTachometerAlt, label: "Mileage", value: `${listing.mileage} ${listing.mileageUnit || 'mi'}` },
                  { icon: FaTag, label: "Condition", value: listing.itemCondition },
                  { icon: FaClock, label: "Availability", value: listing.availability },
                  { icon: FaBarcode, label: "VIN", value: listing.vin },
                  { icon: FaCarAlt, label: "Body Type", value: listing.bodyType },
                  { icon: FaPalette, label: "Color", value: listing.color },
                  { icon: FaGears, label: "Drive Wheel", value: listing.driveWheelConfiguration },
                  { icon: FaDoorClosed, label: "Doors", value: listing.numberOfDoors },
                  { icon: FaGasPump, label: "Fuel Type", value: listing.fuelType },
                  { icon: FaCarSide, label: "Engine", value: listing.vehicleEngine },
                  { icon: FaUsers, label: "Seating", value: listing.vehicleSeatingCapacity },
                  { icon: FaExchangeAlt, label: "Transmission", value: listing.vehicleTransmission },
                  { icon: FaCogs, label: "Cylinders", value: listing.cylinders },
                ].filter(field => field.value).map(({ icon: Icon, label, value }, index) => (
                  <div key={index} className="flex flex-col items-center gap-3 p-4 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100/50 hover:from-red-50 hover:to-orange-50 transition-all duration-300 border border-gray-100 hover:border-red-200 hover:shadow-md">
                    <Icon className="text-[#DC2626] text-lg" />
                    <div className="text-center">
                      <p className="text-xs text-gray-600 font-semibold uppercase tracking-wider mb-1">{label}</p>
                      <p className="text-sm font-extrabold text-gray-900 truncate">{value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Features Section */}
            {listing.carFeature && listing.carFeature.length > 0 && (
              <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8 lg:p-10 border border-gray-100">
                <div className="flex items-center gap-4 mb-8">
                  <div className="h-1.5 w-20 bg-gradient-to-r from-[#DC2626] to-[#B91C1C] rounded-full"></div>
                  <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">Features</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {Array.isArray(listing.carFeature) ? listing.carFeature.map((feature, index) => (
                    <div key={index} className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-gray-50 to-gray-100/50 hover:from-red-50 hover:to-orange-50 transition-all duration-300 border border-gray-100 hover:border-red-200 hover:shadow-md">
                      <FaCheckCircle className="text-green-600 text-xl" />
                      <span className="text-gray-900 font-semibold">{feature}</span>
                    </div>
                  )) : (
                    <p className="text-gray-600">No features listed</p>
                  )}
                </div>
              </div>
            )}

            {/* Safety Features Section */}
            {listing.carSafetyFeature && listing.carSafetyFeature.length > 0 && (
              <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8 lg:p-10 border border-gray-100">
                <div className="flex items-center gap-4 mb-8">
                  <div className="h-1.5 w-20 bg-gradient-to-r from-[#DC2626] to-[#B91C1C] rounded-full"></div>
                  <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">Safety Features</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {Array.isArray(listing.carSafetyFeature) ? listing.carSafetyFeature.map((safety, index) => (
                    <div key={index} className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50/50 hover:from-blue-100 hover:to-cyan-100 transition-all duration-300 border border-blue-100 hover:border-blue-300 hover:shadow-md">
                      <FaCheckCircle className="text-blue-600 text-xl" />
                      <span className="text-gray-900 font-semibold">{safety}</span>
                    </div>
                  )) : (
                    <p className="text-gray-600">No safety features listed</p>
                  )}
                </div>
              </div>
            )}

            {/* Description Section */}
            {listing.description && (
              <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8 lg:p-10 border border-gray-100">
                <div className="flex items-center gap-4 mb-8">
                  <div className="h-1.5 w-20 bg-gradient-to-r from-[#DC2626] to-[#B91C1C] rounded-full"></div>
                  <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">Description</h2>
                </div>
                <div
                  className="prose prose-lg max-w-none"
                  dangerouslySetInnerHTML={{ __html: listing.description }}
                ></div>
              </div>
            )}
          </div>

          {/* Right Column - Contact & Booking */}
          <div className="space-y-6 lg:sticky lg:top-8 self-start">
            {/* Contact Box */}
            <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8 border-2 border-gray-100">
              <div className="flex items-center gap-4 mb-8">
                <div className="h-1.5 w-16 bg-gradient-to-r from-[#DC2626] to-[#B91C1C] rounded-full"></div>
                <h3 className="text-3xl font-extrabold text-gray-900">Contact</h3>
              </div>
              
              {/* Phone Number */}
              <a
                href="tel:+2348139851110"
                className="flex items-center gap-5 p-5 rounded-2xl bg-gradient-to-r from-[#DC2626] to-[#B91C1C] text-white mb-4 hover:from-[#B91C1C] hover:to-[#991B1B] transition-all duration-300 transform hover:scale-105 shadow-xl group"
              >
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center group-hover:bg-white/30 transition-colors shadow-lg">
                  <FaPhone className="text-white text-2xl" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-white/90 uppercase tracking-wider mb-1">Tap to dial</p>
                  <p className="text-xl font-extrabold">+234 813 985 1110</p>
                </div>
              </a>

              {/* WhatsApp */}
              <a
                href="https://wa.me/2348139851110"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-5 p-5 rounded-2xl bg-gradient-to-r from-green-500 to-green-600 text-white mb-6 hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:scale-105 shadow-xl group"
              >
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center group-hover:bg-white/30 transition-colors shadow-lg">
                  <FaWhatsapp className="text-white text-2xl" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-white/90 uppercase tracking-wider mb-1">Tap to chat</p>
                  <p className="text-xl font-extrabold">WhatsApp</p>
                </div>
              </a>

              {/* Share Section */}
              <div className="pt-6 border-t-2 border-gray-200">
                <div className="flex items-center gap-3 mb-5">
                  <FaShareAlt className="text-gray-700 text-xl" />
                  <h4 className="text-xl font-extrabold text-gray-900">Share</h4>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="w-12 h-12 bg-green-100 hover:bg-green-500 text-green-600 hover:text-white rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-md"
                  >
                    <FaWhatsapp className="text-xl" />
                  </button>
                  <button
                    onClick={() => handleShare('facebook')}
                    className="w-12 h-12 bg-blue-100 hover:bg-blue-500 text-blue-600 hover:text-white rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-md"
                  >
                    <FaFacebookF className="text-xl" />
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="w-12 h-12 bg-blue-100 hover:bg-blue-700 text-blue-700 hover:text-white rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-md"
                  >
                    <FaLinkedin className="text-xl" />
                  </button>
                  <button
                    onClick={() => handleShare('email')}
                    className="w-12 h-12 bg-gray-100 hover:bg-gray-600 text-gray-600 hover:text-white rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-md"
                  >
                    <FaEnvelope className="text-xl" />
                  </button>
                </div>
              </div>
            </div>

            {/* Related Cars */}
            <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8 border border-gray-100">
              <div className="flex items-center gap-4 mb-6">
                <div className="h-1.5 w-16 bg-gradient-to-r from-[#DC2626] to-[#B91C1C] rounded-full"></div>
                <h3 className="text-2xl md:text-3xl font-extrabold text-gray-900">Related Cars</h3>
              </div>
              <RelatedCars />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
